import { NgModule, DoBootstrap, ApplicationRef, APP_INITIALIZER, inject, LOCALE_ID, isDevMode, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core'
import { UpgradeModule } from '@angular/upgrade/static'
import { AppComponent } from './app.component'
import { RouterModule, TitleStrategy } from '@angular/router'
import { routes } from './routes/index'
import { APP_BASE_HREF, CommonModule } from '@angular/common'
import { BrowserModule } from '@angular/platform-browser'
import { MainComponent } from './main.component'
import { ServiceWorkerModule, SwUpdate } from '@angular/service-worker'
import { UpgradedModule } from './upgrade/upgraded.module'

import { setupAppProvider } from './upgrade/route.service'
import epf from '../../app';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'
import { RouterEventsService } from './upgrade/router.events.service'
import { provideRisrCoreConfig } from '@fry-it/risr-core';
import { RisrTitleStrategy } from '@fry-it/risr-core';
import { provideFieldExpanders } from '@fry-it/risr-features/common';
import { RisrAiAssistantHostComponent } from '@fry-it/risr-features/common';
import { provideNativeDateAdapter } from '@angular/material/core'
import { DOC_VIEW_HANDLER } from '@fry-it/risr-features/reports';
import { KaizenDocViewHandler } from './kaizen-doc-view-handler';
import { buildAngularJsLocaleData } from './locale-bridge';

@NgModule({
    imports: [
        BrowserAnimationsModule,
        BrowserModule,
        CommonModule,
        RouterModule.forRoot(routes, {
            useHash: false,

            enableTracing: false,
            bindToComponentInputs: true,
            enableViewTransitions: false,
        }),
        UpgradeModule,
        UpgradedModule,
        RisrAiAssistantHostComponent,
        ServiceWorkerModule.register('ngsw-worker.js', {
          enabled: !isDevMode(),
          // Register the ServiceWorker as soon as the application is stable
          // or after 30 seconds (whichever comes first).
          registrationStrategy: 'registerWhenStable:30000'
        }),

    ],
    declarations: [
        AppComponent,
        MainComponent,
    ],
    providers: [
        SwUpdate,
        provideFieldExpanders(),
        {
            provide: '$scope',
            useFactory: i => i.get('$rootScope'),
            deps: ['$injector']
        },
        {
            provide: APP_INITIALIZER,
            useFactory: () => {
                return () => {

                }
            },
            deps: [RouterEventsService],
            multi: true
        },
        {
            provide: APP_BASE_HREF,
            useFactory: () => {
              const locale = inject(LOCALE_ID);
              console.log("SETUP: Locale", locale);
              let basePath;
              if (window['kzConfig']?.languages === undefined) {
                 basePath = '/';
              } else {
                basePath = window['kzConfig'].languages[locale] || document.getElementsByTagName('base')[0].getAttribute('href');
              }
              console.log('SETUP: Base path is', basePath);
              return basePath;
            },
            deps: [LOCALE_ID],
        },
        setupAppProvider,
        provideRisrCoreConfig({ product: 'kaizen' }),
        { provide: TitleStrategy, useClass: RisrTitleStrategy },
        provideNativeDateAdapter(),
        { provide: DOC_VIEW_HANDLER, useClass: KaizenDocViewHandler },
    ],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule implements DoBootstrap {
    private readonly upgrade: UpgradeModule
    private readonly localeId: string

    constructor(upgrade: UpgradeModule) {
        this.upgrade = upgrade
        this.localeId = inject(LOCALE_ID)
    }

    ngDoBootstrap(appRef: ApplicationRef) {
        appRef.bootstrap(AppComponent)

        // Expose the locale Angular was compiled with (via $localize / LOCALE_ID)
        // so the upgraded AngularJS layer can mirror it instead of guessing from
        // base href / kzConfig. This keeps both layers in the same language, e.g.
        // when serving with `ng serve --configuration=fr-CA`.
        window['kzAngularLocale'] = this.localeId
        document.documentElement.lang = this.localeId

        // Derive AngularJS's $locale data (month/day names, date & number formats)
        // from Angular's own registered locale data, so legacy date/number filters
        // match the Angular app for every locale without hand-maintained tables.
        window['kzAngularLocaleData'] = buildAngularJsLocaleData(this.localeId)

        // Get the bootstrap process started first
        this.upgrade.bootstrap(document.documentElement, [epf], { strictDi: true })
    }
}